import { useLazyQuery, useQuery } from "@apollo/client";
import { useMutation } from "@apollo/client";

import RichTextEditor from "react-rte";
import {
  Button,
  Card,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import { Box } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";

import { useState, useEffect } from "react";
import { CREATE_EXE_ROUTINE } from "../../gql/plan";

import imageService from "../../services/image";
import { useRef } from "react";

import {
  ALL_PLANS,
  EXE_TARGET,
  EXP_LEVEL,
  SUBSCRIPTION_TYPE,
} from "../../gql/mixed";

const fileTypes = [
  "image/apng",
  "image/bmp",
  "image/gif",
  "image/jpeg",
  "image/pjpeg",
  "image/png",
  "image/svg+xml",
  "image/tiff",
  "image/webp",
  "image/x-icon",
];

const toolbarConfig = {
  display: [
    "INLINE_STYLE_BUTTONS",
    "BLOCK_TYPE_BUTTONS",
    "LINK_BUTTONS",
    "BLOCK_TYPE_DROPDOWN",
    "HISTORY_BUTTONS",
  ],
  INLINE_STYLE_BUTTONS: [
    { label: "Bold", style: "BOLD", className: "custom-css-class" },
    { label: "Italic", style: "ITALIC" },
    { label: "Underline", style: "UNDERLINE" },
  ],
  BLOCK_TYPE_DROPDOWN: [
    { label: "Normal", style: "unstyled" },
    { label: "Heading Large", style: "header-one" },
    { label: "Heading Medium", style: "header-two" },
    { label: "Heading Small", style: "header-three" },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: "UL", style: "unordered-list-item" },
    { label: "OL", style: "ordered-list-item" },
  ],
};

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const CreateExeRoutine = ({ handleClose, routineAlert }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [textValue, setTextValue] = useState(RichTextEditor.createEmptyValue());
  const [textValueMM, setTextValueMM] = useState(RichTextEditor.createEmptyValue());

  const { data: exeTargets } = useQuery(EXE_TARGET);
  const { data: expLevels } = useQuery(EXP_LEVEL);
  const { data: subscriptionTypes } = useQuery(SUBSCRIPTION_TYPE);

  const fileInputRef = useRef(null);

  const imageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      let img = e.target.files[0];
      if (!fileTypes.includes(img.type)) {
        setErrors({ ...errors, image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)" });
        return;
      }
      if (img.size > 10485760) {
        setErrors({ ...errors, image_url: "Image file size must be smaller than 10MB." });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      setImageUploading(true);
      setErrors({ ...errors, image_url: "" });
      try {
        const result = await imageService.uploadFile(img);
        setValues({ ...values, image_url: result.imageUploadUrl });
      } catch (err) {
        setErrors({ ...errors, image_url: err?.message || "Upload failed." });
      } finally {
        setImageUploading(false);
      }
    }
  };

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    delete errors[prop];
    setErrors(errors);
  };

  const [createRoutine] = useMutation(CREATE_EXE_ROUTINE, {
    onError: (error) => {
      console.log("error : ", error);
      setLoading(false);
    },
    onCompleted: () => {
      setValues({});
      setTextValue(RichTextEditor.createEmptyValue());
      setErrors({});
      routineAlert("New Plan has been added");
      setLoading(false);
      handleClose();
    },
    refetchQueries: [ALL_PLANS],
  });

  const handleCreate = async () => {
    setLoading(true);
    setErrors({});
    let isErrorExit = false;
    let errorObject = {};
    if (!values.plan_name) { errorObject.plan_name = "Plan name is required"; isErrorExit = true; }
    if (!values.fk_user_subscription_level_id) { errorObject.fk_user_subscription_level_id = "Subscription level is required."; isErrorExit = true; }
    if (!values.fk_exercise_target_id) { errorObject.fk_exercise_target_id = "Exercise Target is required."; isErrorExit = true; }
    if (!values.fk_experience_level_id) { errorObject.fk_experience_level_id = "Experience Level is required."; isErrorExit = true; }
    if (!values.description) { errorObject.description = "Description is required"; isErrorExit = true; }
    if (!values.description_mm) { errorObject.description_mm = "Description MM is required"; isErrorExit = true; }
    if (!values.routine_days_in_a_week) { errorObject.routine_days_in_a_week = "Routine days is required"; isErrorExit = true; }
    if (isErrorExit) { setErrors(errorObject); setLoading(false); return; }
    try {
      await createRoutine({ variables: { ...values } });
    } catch (error) {
      console.log("error : ", error);
    }
  };

  const handleCloseClearData = () => {
    setValues({});
    setErrors({});
    handleClose();
  };

  const onChange = (value) => {
    setTextValue(value);
    setValues({ ...values, description: value.toString("html") });
  };

  const onChangeMM = (value) => {
    setTextValueMM(value);
    setValues({ ...values, description_mm: value.toString("html") });
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Create Plan
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Fill in the details to create a new workout plan
          </Typography>
        </Box>
        <Button onClick={handleCloseClearData} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      <Box sx={{ maxWidth: 960, mx: "auto", px: 3, py: 3 }}>
        {/* Media Upload Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Cover Image
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Upload Zone */}
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: 200,
                height: 200,
                border: errors.image_url ? "2px dashed #d32f2f" : "2px dashed #ddd",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                bgcolor: "#fafafa",
                transition: "all 0.2s",
                flexShrink: 0,
                "&:hover": { borderColor: "#ff5722", bgcolor: "rgba(255,87,34,0.03)" },
              }}
            >
              {imageUploading ? (
                <CircularProgress size={32} sx={{ color: "#ff5722" }} />
              ) : imagePreview ? (
                <Box component="img" src={imagePreview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px" }} />
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 36, color: "#bbb", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" textAlign="center" px={1}>
                    Click to upload image
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    PNG, JPG, GIF up to 10MB
                  </Typography>
                </>
              )}
            </Box>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={imageChange} style={{ display: "none" }} />

            <Box>
              {imagePreview ? (
                <Chip label="Image selected ✓" color="success" size="small" sx={{ mb: 1 }} />
              ) : (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Upload a cover image for this plan
                </Typography>
              )}
              {errors.image_url && (
                <Typography variant="caption" color="error">{errors.image_url}</Typography>
              )}
              {imageUploading && (
                <Typography variant="caption" color="text.secondary">Uploading image...</Typography>
              )}
            </Box>
          </Box>
        </Card>

        {/* Plan Details Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Plan Details
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="plan_name"
              label="Plan Name *"
              fullWidth
              value={values.plan_name || ""}
              onChange={handleChange("plan_name")}
              error={!!errors.plan_name}
              helperText={errors.plan_name}
            />
            <TextField
              type="number"
              id="routine_days_in_a_week"
              label="Routine Days In A Week *"
              fullWidth
              value={values.routine_days_in_a_week || ""}
              onChange={handleChange("routine_days_in_a_week")}
              error={!!errors.routine_days_in_a_week}
              helperText={errors.routine_days_in_a_week}
            />
            <FormControl fullWidth>
              <InputLabel>Subscription Level *</InputLabel>
              <Select
                label="Subscription Level *"
                value={values.fk_user_subscription_level_id || ""}
                onChange={handleChange("fk_user_subscription_level_id")}
                error={!!errors.fk_user_subscription_level_id}
              >
                <MenuItem value="" disabled>Select level</MenuItem>
                {subscriptionTypes?.user_subscription_level?.map((level) => (
                  <MenuItem key={level.id} value={level.id}>{level.subscription_type}</MenuItem>
                ))}
              </Select>
              {errors.fk_user_subscription_level_id && <FormHelperText error>{errors.fk_user_subscription_level_id}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Exercise Target *</InputLabel>
              <Select
                label="Exercise Target *"
                value={values.fk_exercise_target_id || ""}
                onChange={handleChange("fk_exercise_target_id")}
                error={!!errors.fk_exercise_target_id}
              >
                <MenuItem value="" disabled>Select target</MenuItem>
                {exeTargets?.exercise_targets?.map((level) => (
                  <MenuItem key={level.id} value={level.id}>{level.target_name}</MenuItem>
                ))}
              </Select>
              {errors.fk_exercise_target_id && <FormHelperText error>{errors.fk_exercise_target_id}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Experience Level *</InputLabel>
              <Select
                label="Experience Level *"
                value={values.fk_experience_level_id || ""}
                onChange={handleChange("fk_experience_level_id")}
                error={!!errors.fk_experience_level_id}
              >
                <MenuItem value="" disabled>Select level</MenuItem>
                {expLevels?.experience_level?.map((level) => (
                  <MenuItem key={level.id} value={level.id}>{level.name}</MenuItem>
                ))}
              </Select>
              {errors.fk_experience_level_id && <FormHelperText error>{errors.fk_experience_level_id}</FormHelperText>}
            </FormControl>
          </Box>
        </Card>

        {/* Descriptions Section */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Descriptions
          </Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                English *
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChange}
                value={textValue}
                toolbarConfig={toolbarConfig}
              />
              {errors.description && <FormHelperText error>{errors.description}</FormHelperText>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Myanmar *
              </Typography>
              <RichTextEditor
                className="description-text"
                onChange={onChangeMM}
                value={textValueMM}
                toolbarConfig={toolbarConfig}
              />
              {errors.description_mm && <FormHelperText error>{errors.description_mm}</FormHelperText>}
            </Box>
          </Box>
        </Card>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={handleCloseClearData} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={loading || imageUploading}
            onClick={handleCreate}
            sx={{
              minWidth: 120,
              bgcolor: "#ff5722",
              "&:hover": { bgcolor: "#e64a19" },
              "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
            {loading ? "Creating..." : "Create Plan"}
          </Button>
        </Box>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="success">{showAlert.message}</Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="error">{showAlert.message}</Alert>
      )}
    </Box>
  );
};

export default CreateExeRoutine;
